import React, { useEffect, useState } from "react";
import { Settings } from "lucide-react";
import fetchWithAuth from "../fetchWithAuth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";

export default function GlobalSettings() {
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [hasApiKey, setHasApiKey] = useState(false);
  const [searchType, setSearchType] = useState(2);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setLoaded(false);
    setApiKey("");
    setError("");
    setSaved(false);
    const load = async () => {
      try {
        const res = await fetchWithAuth("/api/settings");
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Не удалось загрузить настройки");
        if (active) {
          setSearchType(data.arsenkinSearchType);
          setHasApiKey(data.hasArsenkinApiKey);
          setLoaded(true);
        }
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [open]);

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const res = await fetchWithAuth("/api/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          arsenkinSearchType: searchType,
          ...(apiKey.trim() ? { arsenkinApiKey: apiKey.trim() } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Не удалось сохранить настройки");
      setHasApiKey(data.hasArsenkinApiKey);
      setApiKey("");
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) setApiKey(""); }}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" className="text-primary gap-2">
          <Settings className="h-4 w-4" aria-hidden="true" />
          Глобальные настройки
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Глобальные настройки</DialogTitle>
          <DialogDescription>
            Настройки Arsenkin применяются к парсингу всех проектов.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-5">
          <fieldset disabled={loading || saving || !loaded} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="arsenkin-api-key" className="block text-sm font-medium">API-ключ Arsenkin</label>
              <Input
                id="arsenkin-api-key"
                type="password"
                autoComplete="new-password"
                value={apiKey}
                onChange={(event) => { setApiKey(event.target.value); setSaved(false); }}
                placeholder={hasApiKey ? "Ключ уже задан" : "Введите API-ключ"}
                aria-describedby="arsenkin-key-help"
              />
              <p id="arsenkin-key-help" className="text-sm text-gray-500">
                {hasApiKey ? "Оставьте поле пустым, чтобы сохранить текущий ключ, или введите новый для замены." : "Ключ используется для отправки запросов в Arsenkin."}
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="arsenkin-search-type" className="block text-sm font-medium">Тип парсинга</label>
              <select
                id="arsenkin-search-type"
                value={searchType}
                onChange={(event) => { setSearchType(Number(event.target.value)); setSaved(false); }}
                className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                <option value={1}>Яндекс XML</option>
                <option value={2}>Яндекс Desktop</option>
                <option value={3}>Яндекс Mobile</option>
              </select>
            </div>
          </fieldset>
          {loading && <p role="status" className="text-sm text-gray-500">Загрузка настроек…</p>}
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          {saved && <p role="status" className="text-sm text-green-700">Настройки сохранены</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Закрыть</Button>
            <Button type="submit" disabled={!loaded || loading || saving}>{saving ? "Сохранение…" : "Сохранить"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
